import React, { useEffect, useMemo, useState } from 'react';
import type { SessionLogEntry, Task, Project, TaskSession } from '../types';
import { Storage } from '../utils/storage.ts';
import { getSessionLogs } from '../utils/sessionLog.ts';
import { totalFocusTime, focusByDay, bestStreak } from '../utils/stats.ts';
import { formatDuration } from '../utils/formatDuration.ts';
import { useTask } from '../contexts/TaskContext.tsx';
import './StatsPage.css';

interface StatsPageProps {
  onBack: () => void;
}

// ---------- Helpers ----------

const RANGE_STORAGE_KEY = 'kf-stats-range';

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoWeekDay(d: Date): number {
  return (d.getDay() + 6) % 7; // 0=Mon … 6=Sun
}

function getFromDate(range: string): string {
  const d = new Date();
  switch (range) {
    case '7d':  d.setDate(d.getDate() - 7);           break;
    case '30d': d.setDate(d.getDate() - 30);           break;
    case '6m':  d.setMonth(d.getMonth() - 6);          break;
    case '1y':  d.setFullYear(d.getFullYear() - 1);    break;
    default:    d.setMonth(d.getMonth() - 6);
  }
  return toDateKey(d);
}

function relativeDate(startTime: number): string {
  const d = new Date(startTime);
  const today = toDateKey(new Date());
  const dStr = toDateKey(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (dStr === today) return `Today, ${hh}:${mm}`;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (dStr === toDateKey(yesterday)) return `Yesterday, ${hh}:${mm}`;
  return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}, ${hh}:${mm}`;
}

// ---------- Sub-components ----------

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className="stat__sub">{sub}</div>}
    </div>
  );
}

// 26-week activity heatmap
const HEAT_PALETTE = [
  'var(--kf-surface-2)',
  'oklch(80% 0.04 254)',
  'oklch(64% 0.10 254)',
  'oklch(54% 0.13 254)',
  'oklch(45% 0.15 254)',
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function heatLevel(minutes: number): number {
  if (minutes === 0) return 0;
  if (minutes < 30) return 1;
  if (minutes < 60) return 2;
  if (minutes < 120) return 3;
  return 4;
}

function ActivityHeatmap({ byDay }: { byDay: Record<string, number> }) {
  const today = new Date();
  const endOfGrid = new Date(today);
  const daysUntilSunday = (6 - isoWeekDay(today) + 7) % 7;
  endOfGrid.setDate(endOfGrid.getDate() + daysUntilSunday);

  const startOfGrid = new Date(endOfGrid);
  startOfGrid.setDate(startOfGrid.getDate() - 182 + 1);

  const cells: { date: string; minutes: number }[] = [];
  for (let i = 0; i < 182; i++) {
    const d = new Date(startOfGrid);
    d.setDate(d.getDate() + i);
    const key = toDateKey(d);
    cells.push({ date: key, minutes: byDay[key] ?? 0 });
  }

  const monthLabels: { label: string; col: number }[] = [];
  let lastMonth = -1;
  for (let i = 0; i < 182; i++) {
    const d = new Date(startOfGrid);
    d.setDate(d.getDate() + i);
    const m = d.getMonth();
    if (m !== lastMonth) {
      monthLabels.push({ label: MONTH_ABBR[m], col: Math.floor(i / 7) });
      lastMonth = m;
    }
  }

  return (
    <div className="heatmap-wrap">
      <div className="heatmap-months">
        {monthLabels.map(({ label, col }) => (
          <span key={`${label}-${col}`} className="heatmap-month" style={{ gridColumn: col + 1 }}>{label}</span>
        ))}
      </div>
      <div className="heatmap-body">
        <div className="heatmap-days">
          {DAY_LABELS.map((d, i) => (
            <span key={d} className="heatmap-day" style={{ visibility: i % 2 === 0 ? 'visible' : 'hidden' }}>{d}</span>
          ))}
        </div>
        <div className="heat">
          {Array.from({ length: 26 }, (_, weekIdx) =>
            Array.from({ length: 7 }, (_, dayIdx) => {
              const { date, minutes } = cells[weekIdx * 7 + dayIdx];
              const level = heatLevel(minutes);
              const tooltip = minutes > 0 ? `${date} — ${minutes}m focused` : date;
              return (
                <div
                  key={date}
                  className="heat__cell"
                  style={{ background: HEAT_PALETTE[level] }}
                  data-tooltip={tooltip}
                />
              );
            })
          )}
        </div>
      </div>
      <div className="legend">
        <span className="muted">Less</span>
        <span className="legend__dots">
          {HEAT_PALETTE.map((color, i) => (
            <i key={i} style={{ background: color }} />
          ))}
        </span>
        <span className="muted">More</span>
      </div>
    </div>
  );
}

// 7-day bar chart (current ISO week, always — not range-filtered)
function WeekBars({ byDay }: { byDay: Record<string, number> }) {
  const today = new Date();
  const todayKey = toDateKey(today);
  const currentDayOfWeek = isoWeekDay(today);

  const weekDays = DAY_LABELS.map((label, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - currentDayOfWeek + i);
    const key = toDateKey(d);
    return { label, key, minutes: byDay[key] ?? 0 };
  });

  const maxMinutes = Math.max(...weekDays.map(d => d.minutes), 1);
  const totalMinutes = weekDays.reduce((s, d) => s + d.minutes, 0);

  return (
    <>
      <div className="stats__card-head">
        <h3>This week</h3>
        <span className="muted">{(totalMinutes / 60).toFixed(1)}h focused</span>
      </div>
      <div className="weekbars">
        {weekDays.map(({ label, key, minutes }) => (
          <div className="weekbar" key={key}>
            <div className="weekbar__col">
              <div
                className="weekbar__fill"
                style={{
                  height: `${(minutes / maxMinutes) * 100}%`,
                  background: key === todayKey ? 'var(--kf-work)' : 'var(--kf-surface-2)',
                }}
              />
            </div>
            <div className="weekbar__day">{label}</div>
            <div className="weekbar__h">{minutes > 0 ? `${minutes}m` : '—'}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// By-project horizontal bars (top 4, range-filtered via task.sessions if available)
function ProjectBars({ tasks, projects, fromDate }: { tasks: Task[]; projects: Project[]; fromDate: string }) {
  const PROJECT_COLORS = [
    'var(--kf-work)',
    'var(--kf-break)',
    'var(--kf-long-break)',
    'var(--kf-muted)',
  ];

  const projectTotals = useMemo(() => {
    const map: Record<string, { name: string; seconds: number }> = {};

    tasks.forEach(task => {
      // Use date-filtered sessions if available; fall back to all-time total
      const seconds = task.sessions && task.sessions.length > 0
        ? task.sessions.filter(s => s.date >= fromDate).reduce((sum, s) => sum + s.duration, 0)
        : task.totalTimeSpent; // fallback: all-time (no date data available)

      if (seconds === 0) return;
      const key = task.projectId ?? '__none__';
      const name = task.projectId
        ? (projects.find(p => p.id === task.projectId)?.name ?? 'Unknown')
        : 'General';
      if (!map[key]) map[key] = { name, seconds: 0 };
      map[key].seconds += seconds;
    });

    return Object.values(map)
      .filter(p => p.seconds > 0)
      .sort((a, b) => b.seconds - a.seconds)
      .slice(0, 4);
  }, [tasks, projects, fromDate]);

  const maxSeconds = Math.max(...projectTotals.map(p => p.seconds), 1);

  if (projectTotals.length === 0) {
    return <div className="bars"><span className="muted">No project data for this period.</span></div>;
  }

  return (
    <div className="bars">
      {projectTotals.map((proj, i) => {
        const pct = Math.round((proj.seconds / maxSeconds) * 100);
        const color = PROJECT_COLORS[i % PROJECT_COLORS.length];
        return (
          <div className="bar" key={proj.name}>
            <div className="bar__head">
              <span><i className="bar__pip" style={{ background: color }} />{proj.name}</span>
              <span className="bar__hrs">{(proj.seconds / 3600).toFixed(1)}h</span>
            </div>
            <div className="bar__track">
              <div className="bar__fill" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Recent sessions table
function RecentSessions({ tasks, projects, fromDate }: { tasks: Task[]; projects: Project[]; fromDate: string }) {
  const recent = useMemo(() => {
    const taskSessions = Storage.read<TaskSession[]>('kf-task-sessions', []);
    return taskSessions
      .filter(ts => ts.date >= fromDate && ts.duration > 0)
      .sort((a, b) => b.startTime - a.startTime)
      .slice(0, 20);
  }, [fromDate]);

  if (recent.length === 0) {
    return <span className="muted">No sessions in this period.</span>;
  }

  return (
    <table className="sessions">
      <thead>
        <tr><th>Task</th><th>Project</th><th>Duration</th><th>When</th></tr>
      </thead>
      <tbody>
        {recent.map(ts => {
          const task = tasks.find(t => t.id === ts.taskId);
          const project = task?.projectId ? projects.find(p => p.id === task.projectId) : undefined;
          const dur = formatDuration(ts.duration) || `${Math.round(ts.duration / 60)}m`;
          return (
            <tr key={ts.sessionId}>
              <td>{task?.text ?? '—'}</td>
              <td>{project ? <span className="chip">{project.name}</span> : <span className="muted">—</span>}</td>
              <td className="num">{dur}</td>
              <td className="muted">{relativeDate(ts.startTime)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Empty state — shown when the user has fewer than 5 total sessions
function EmptyState({ onBack }: { onBack: () => void }) {
  return (
    <div className="stats-page__empty">
      <p className="stats-page__empty-line">Your stats begin once you've finished five sessions.</p>
      <button className="stats-page__empty-cta" onClick={onBack}>Start a session →</button>
    </div>
  );
}

// ---------- Main component ----------

const RANGES = ['7d', '30d', '6m', '1y'] as const;

const StatsPage: React.FC<StatsPageProps> = ({ onBack }) => {
  const { tasks, projects } = useTask();
  const [logs, setLogs] = useState<SessionLogEntry[]>([]);
  const [range, setRange] = useState<string>(
    () => localStorage.getItem(RANGE_STORAGE_KEY) ?? '6m'
  );

  useEffect(() => {
    setLogs(getSessionLogs());
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'stats_page_viewed', { event_category: 'pomodoro_timer' });
    }
  }, []);

  const handleRangeChange = (r: string) => {
    setRange(r);
    localStorage.setItem(RANGE_STORAGE_KEY, r);
  };

  // All-time check for the empty state gate
  const allTimePomodoroCount = useMemo(
    () => logs.filter(e => e.sessionType === 'pomodoro').length,
    [logs]
  );

  const fromDate = useMemo(() => getFromDate(range), [range]);

  const filteredLogs = useMemo(
    () => logs.filter(e => e.date >= fromDate),
    [logs, fromDate]
  );

  const pomodoroLogs = useMemo(
    () => filteredLogs.filter(e => e.sessionType === 'pomodoro'),
    [filteredLogs]
  );

  const allTimeFocus = useMemo(() => totalFocusTime(filteredLogs), [filteredLogs]);
  const bestStreakVal = useMemo(() => bestStreak(filteredLogs), [filteredLogs]);
  const completionRate = useMemo(() => {
    if (pomodoroLogs.length === 0) return 0;
    const completed = pomodoroLogs.filter(e => e.completionStatus === 'completed').length;
    return Math.round((completed / pomodoroLogs.length) * 100);
  }, [pomodoroLogs]);

  // Heatmap always shows 26 weeks regardless of range
  const byDay = useMemo(() => focusByDay(logs, 182), [logs]);

  const rangeSub = range === '7d' ? 'last 7 days'
    : range === '30d' ? 'last 30 days'
    : range === '6m' ? 'last 6 months'
    : 'last year';

  if (allTimePomodoroCount < 5) {
    return (
      <div className="stats-page">
        <div className="stats-page__topbar">
          <button className="stats-page__back" onClick={onBack}>← Timer</button>
        </div>
        <EmptyState onBack={onBack} />
      </div>
    );
  }

  return (
    <div className="stats-page">
      <div className="stats-page__topbar">
        <button className="stats-page__back" onClick={onBack}>← Timer</button>
      </div>

      <div className="stats stats--dashboard">
        <header className="stats__head">
          <div>
            <div className="stats__eyebrow">FOCUS · {rangeSub.toUpperCase()}</div>
            <h1 className="stats__title">Your momentum</h1>
          </div>
          <div className="seg" role="group" aria-label="Time range">
            {RANGES.map(r => (
              <button
                key={r}
                className="seg__btn"
                data-active={r === range}
                onClick={() => handleRangeChange(r)}
                aria-pressed={r === range}
              >
                {r}
              </button>
            ))}
          </div>
        </header>

        <div className="stats__grid">
          <StatCard
            label="Total focus"
            value={formatDuration(allTimeFocus) || '0m'}
            sub={`${pomodoroLogs.length} sessions`}
            accent="var(--kf-work)"
          />
          <StatCard
            label="Sessions"
            value={String(pomodoroLogs.length)}
            sub={pomodoroLogs.length > 0 ? `${(pomodoroLogs.length / Math.max(1, Math.round(getDateRangeDays(range)))).toFixed(1)} daily avg` : undefined}
          />
          <StatCard
            label="Best streak"
            value={`${bestStreakVal}d`}
            accent="var(--kf-long-break)"
          />
          <StatCard
            label="Completion"
            value={`${completionRate}%`}
            sub="sessions finished"
          />
        </div>

        <section className="stats__card">
          <div className="stats__card-head">
            <h3>Activity</h3>
            <div className="legend">
              <span className="muted">Less</span>
              <span className="legend__dots">
                {HEAT_PALETTE.map((color, i) => (
                  <i key={i} style={{ background: color }} />
                ))}
              </span>
              <span className="muted">More</span>
            </div>
          </div>
          <ActivityHeatmap byDay={byDay} />
        </section>

        <div className="stats__row">
          <section className="stats__card">
            <div className="stats__card-head">
              <h3>By project</h3>
              <span className="muted">{rangeSub}</span>
            </div>
            <ProjectBars tasks={tasks} projects={projects} fromDate={fromDate} />
          </section>

          <section className="stats__card">
            <WeekBars byDay={byDay} />
          </section>
        </div>

        <section className="stats__card">
          <div className="stats__card-head">
            <h3>Recent sessions</h3>
            <span className="muted">{rangeSub}</span>
          </div>
          <RecentSessions tasks={tasks} projects={projects} fromDate={fromDate} />
        </section>
      </div>
    </div>
  );
};

function getDateRangeDays(range: string): number {
  switch (range) {
    case '7d':  return 7;
    case '30d': return 30;
    case '6m':  return 182;
    case '1y':  return 365;
    default:    return 182;
  }
}

export default StatsPage;
